import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { SalesTargetService } from '../../services/salesTarget.service';
import { SalesBoardBlock, SalesTeamBoard } from '../../interfaces/sales/salesTarget.interface';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import { DashEmpty, DashSection } from './personal/DashSection';
import StatTile from './charts/StatTile';
import HBars from './charts/HBars';
import Columns from './charts/Columns';

const fmtInt = (n: number) => Math.round(n).toLocaleString('en-US');
const fmtPct = (n: number | null) => (n === null || n === undefined ? '-' : `${n.toFixed(1)}%`);
const pctTone = (n: number | null) => (n === null ? 'default' : n >= 100 ? 'good' : n >= 60 ? 'default' : 'warning');

/** A capped list block: rows, plus "and N more" when the server holds more than it sent. */
function BoardList<T>({ block, empty, testId, keyOf, render }: {
    block: SalesBoardBlock<T> | undefined; empty: string; testId: string; keyOf: (r: T) => string; render: (r: T) => React.ReactNode;
}) {
    if (!block || block.rows.length === 0) return <DashEmpty text={empty} />;
    const more = block.total - block.rows.length;
    return (
        <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} data-testid={testId}>
            {block.rows.map(r => (
                <Stack key={keyOf(r)} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>{render(r)}</Stack>
            ))}
            {more > 0 && <Typography variant="caption" color="text.secondary">and {more} more</Typography>}
        </Stack>
    );
}

/**
 * Sales Manager / Sales Head home (lane S2). KPI tile strip, chart sections (monthly trend, teams against target,
 * people against target), then the action lists (quotations waiting for approval, unassigned work). Every figure the
 * server sends is already limited to the viewer's sales scope: a manager sees the teams, a head the department.
 */
const SalesTeamDashboard: React.FC = () => {
    const canManageTargets = usePermission(PERMISSIONS.SALES_TARGET_MANAGE);
    const canApprove = usePermission(PERMISSIONS.QUOTATION_APPROVE);
    const [data, setData] = useState<SalesTeamBoard | null>(null);
    const [failed, setFailed] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) { setFailed(true); setLoading(false); return; }
        try {
            setData(await SalesTargetService.teamBoard(institutionId));
            setFailed(false);
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

    const k = data?.kpis;
    const isHead = !!data && data.level !== 'MANAGER';
    const title = data?.level === 'ALL' ? 'Sales Dashboard' : isHead ? 'Sales Head Dashboard' : 'Sales Manager Dashboard';

    return (
        <Box data-help-page="sales.teamDashboard.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">{title}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-help-section="sales.teamDashboard.shortcuts">
                    <Button data-help="sales.teamDashboard.targets" component={RouterLink} to="/sales/targets" size="small" variant="contained">Targets</Button>
                    <Button data-help="sales.teamDashboard.quotations" component={RouterLink} to="/sales/quotations" size="small" variant="outlined">Quotations</Button>
                    <Button data-help="sales.teamDashboard.orders" component={RouterLink} to="/sales/orders" size="small" variant="outlined">Sales orders</Button>
                </Stack>
            </Stack>

            {failed && !data && <Alert severity="warning" sx={{ mb: 2 }} data-help-section="sales.teamDashboard.notice">Sales team figures are unavailable right now.</Alert>}

            {data && k && (
                <>
                    <Paper data-help-section="sales.teamDashboard.kpi" variant="outlined" sx={{ p: 1, mb: 2 }}>
                        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }} data-testid="sales-team-kpi-strip">
                            <StatTile data-help="sales.teamDashboard.tile.monthTarget" label="Month target" unit="৳" value={formatMoney(k.target)} sub={k.target > 0 ? undefined : 'No target set'} />
                            <StatTile data-help="sales.teamDashboard.tile.confirmed" label="Confirmed orders (gross)" unit="৳" value={formatMoney(k.actual)} sub={`${fmtPct(k.achievementPct)} of target, teams only`}
                                tone={pctTone(k.achievementPct)} />
                            <StatTile data-help="sales.teamDashboard.tile.openQuotes" label="Open quotations" value={fmtInt(k.openQuotations)} sub={`৳ ${formatMoney(k.openQuotationValue)}`} />
                            <StatTile data-help="sales.teamDashboard.tile.waitingApproval" label="Waiting your approval" value={fmtInt(k.waitingApprovals)} tone={k.waitingApprovals > 0 ? 'warning' : 'good'} />
                            <StatTile data-help="sales.teamDashboard.tile.openOrders" label="Open orders" value={fmtInt(k.openOrders)} sub="Confirmed or part-delivered" />
                            <StatTile data-help="sales.teamDashboard.tile.overdueAr" label="Overdue receivables" unit="৳" value={formatMoney(k.overdueReceivables)}
                                sub={`${fmtInt(k.overdueInvoices)} invoices`} tone={k.overdueInvoices > 0 ? 'critical' : 'good'} />
                            {isHead && <StatTile data-help="sales.teamDashboard.tile.outsideTeams" label="Sales outside teams" unit="৳" value={formatMoney(k.outsideTeamsActual)} sub="Unassigned or no team" />}
                            {isHead && <StatTile data-help="sales.teamDashboard.tile.unassigned" label="Unassigned" value={fmtInt(k.unassigned)} sub="Quotations and orders" tone={k.unassigned > 0 ? 'warning' : 'good'} />}
                        </Stack>
                    </Paper>

                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, mb: 2 }}>
                        <DashSection data-help-section="sales.teamDashboard.trend" title="Sales by month" subtitle="Confirmed orders (gross), last 6 months">
                            {data.trend.every(t => t.actual === 0)
                                ? <DashEmpty text="No confirmed orders in the last 6 months." />
                                : <Columns data={data.trend.map(t => ({ label: t.month, value: t.actual }))} height={200} colorIndex={1}
                                    yFmt={n => formatMoney(n)} />}
                        </DashSection>
                        <DashSection data-help-section="sales.teamDashboard.teams" title="Teams against target" subtitle="Achievement this month, percent">
                            {data.teams.length === 0
                                ? <DashEmpty text="No teams in your scope." />
                                : <HBars data={data.teams.map(t => ({ label: t.name, value: t.achievementPct ?? 0 }))} colorIndex={2} vFmt={n => `${n.toFixed(0)}%`} />}
                        </DashSection>
                        <DashSection data-help-section="sales.teamDashboard.people" title="People against target" subtitle="Achievement this month, percent">
                            {data.reps.length === 0
                                ? <DashEmpty text="No salespeople in your scope." />
                                : <HBars data={data.reps.slice(0, 10).map(r => ({ label: r.name ?? 'Unknown', value: r.achievementPct ?? 0 }))} colorIndex={4} vFmt={n => `${n.toFixed(0)}%`} />}
                        </DashSection>
                        <DashSection data-help-section="sales.teamDashboard.byPerson" title="Sales by person" subtitle="Confirmed orders (gross) this month">
                            {data.reps.every(r => r.actual === 0)
                                ? <DashEmpty text="No confirmed orders this month." />
                                : <HBars data={data.reps.filter(r => r.actual > 0).slice(0, 10).map(r => ({ label: r.name ?? 'Unknown', value: r.actual }))} colorIndex={0} vFmt={n => formatMoney(n)} />}
                        </DashSection>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
                        <DashSection data-help-section="sales.teamDashboard.approvals" title={`Quotations waiting for approval (${data.approvals.total})`} subtitle="Waiting for you at the current stage, oldest first">
                            <BoardList block={data.approvals} empty="No quotation is waiting for approval." testId="sales-approvals" keyOf={r => r.id}
                                render={r => (
                                    <>
                                        {canApprove
                                            ? <Link component={RouterLink} to="/sales/quotations" underline="hover" sx={{ fontWeight: 600 }}>{r.quotationNumber ?? 'Quotation'}</Link>
                                            : <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.quotationNumber ?? 'Quotation'}</Typography>}
                                        <Typography variant="body2" color="text.secondary">{r.customerName ?? ''}{r.salespersonName ? ` · ${r.salespersonName}` : ''}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="body2">{formatMoney(r.totalAmount)}</Typography>
                                    </>
                                )} />
                        </DashSection>
                        {isHead && (
                            <DashSection data-help-section="sales.teamDashboard.unassigned" title={`Unassigned work (${data.unassignedQueue.total})`} subtitle="Quotations and orders with no salesperson">
                                <BoardList block={data.unassignedQueue} empty="Every open quotation and order has an owner." testId="sales-unassigned"
                                    keyOf={r => `${r.kind}-${r.id}`}
                                    render={r => (
                                        <>
                                            <Chip size="small" label={r.kind === 'ORDER' ? 'Order' : 'Quotation'} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.number ?? ''}</Typography>
                                            <Typography variant="body2" color="text.secondary">{r.customerName ?? ''}</Typography>
                                            <Box sx={{ flex: 1 }} />
                                            <Typography variant="caption" color="text.secondary">{r.date ? formatDate(r.date) : ''}</Typography>
                                        </>
                                    )} />
                            </DashSection>
                        )}
                        {canManageTargets && data.teams.every(t => t.effectiveTarget === 0) && (
                            <DashSection data-help-section="sales.teamDashboard.noTargets" title="Targets not set" subtitle="No team or person has a target this month">
                                <Button data-help="sales.teamDashboard.setTargets" component={RouterLink} to="/sales/targets" size="small">Set targets</Button>
                            </DashSection>
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default SalesTeamDashboard;
